// src/pages/CapturePage/blocks/LongSoundGame.js
//
// A sustained vowel, held as long as the child can, with a balloon that grows
// while the sound lasts. Recorded unprocessed — this is the sample the voice
// measures are taken from later.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import BlockExit from '../BlockExit';
import { captureError, saveVoiceSample } from '../captureApi';
import { startRawRecording } from '../rawRecorder';

export default function LongSoundGame({ session, block, onSaved, onFinish }) {
    const attemptsWanted = block.attempts || 2;
    const already = (session.voice || []).filter((v) => v.kind === 'vowel').length;

    const [attempt, setAttempt] = useState(already);
    const [phase, setPhase] = useState('ready');
    const [elapsed, setElapsed] = useState(0);
    const [level, setLevel] = useState(0);
    const [error, setError] = useState('');
    const recorderRef = useRef(null);
    const tickRef = useRef(null);

    const clearTick = () => {
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
    useEffect(() => clearTick, []);

    const stop = useCallback(async () => {
        clearTick();
        const recorder = recorderRef.current;
        recorderRef.current = null;
        if (!recorder) return;

        setPhase('saving');
        try {
            const audio = await recorder.stop();
            const r = await saveVoiceSample(session.id, {
                kind: 'vowel',
                audio_base64: audio.base64,
                duration_ms: audio.durationMs,
                sample_rate_hz: audio.sampleRate,
            });
            onSaved(r.data);
            const next = attempt + 1;
            setAttempt(next);
            setPhase('ready');
            setElapsed(0);
            setLevel(0);
            if (next >= attemptsWanted) onFinish();
        } catch (e) {
            setError(captureError(e, 'Could not save that sound. Try once more.'));
            setPhase('ready');
        }
    }, [attempt, attemptsWanted, onFinish, onSaved, session.id]);

    const start = async () => {
        setError('');
        try {
            recorderRef.current = await startRawRecording({ onLevel: setLevel });
        } catch (e) {
            setError('The microphone is not available. Check the permission and try again.');
            return;
        }
        setPhase('recording');
        const startedAt = Date.now();
        const max = block.max_seconds || 15;
        tickRef.current = setInterval(() => {
            const seconds = (Date.now() - startedAt) / 1000;
            setElapsed(seconds);
            if (seconds >= max) stop();
        }, 100);
    };

    const target = block.target_seconds || 5;
    // The balloon follows the loudness as well as the clock, so it deflates when
    // the child stops making a sound rather than growing on silence.
    const size = Math.min(1, (elapsed / target) * 0.7 + Math.min(1, level * 4) * 0.3);

    return (
        <div className="cap-child-screen">
            <div className="cap-child-head">
                <span className="cap-child-step">{Math.min(attempt + 1, attemptsWanted)} / {attemptsWanted}</span>
                <h2>{block.child_title?.hi}</h2>
                <p>{block.child_title?.en}</p>
            </div>

            <div className="cap-balloon-area">
                <div className="cap-balloon" style={{ transform: `scale(${0.4 + size * 0.9})` }}>
                    <span role="img" aria-label="balloon">🎈</span>
                </div>
            </div>

            <p className="cap-instruction">
                {block.instruction?.hi} <em>{block.instruction?.en}</em>
            </p>
            {phase === 'recording' ? <p className="cap-big">{elapsed.toFixed(1)}s</p> : null}
            {error ? <p className="cap-error">{error}</p> : null}

            {phase === 'ready' ? (
                <div className="cap-actions">
                    <button type="button" className="cap-btn big" onClick={start}>
                        {attempt === 0 ? 'Start' : 'Once more'}
                    </button>
                    {attempt > 0 ? (
                        <button type="button" className="cap-btn ghost" onClick={onFinish}>That is enough</button>
                    ) : null}
                </div>
            ) : null}

            {phase === 'saving' ? <p className="cap-hint">Saving…</p> : null}
            {phase === 'recording' ? <BlockExit onExit={stop} label="Hold to stop" /> : null}
        </div>
    );
}
